import React, { useEffect, useRef, useState } from "react";
import {
  checkPushNotificationSubscription,
  fetchPushNotificationPublicKey,
  subscribeToPushNotification,
  unsubscribeFromPushNotification,
} from "../../api/api";

import { Switch } from "@material-tailwind/react";
import { useDeviceId } from "../hooks/useDeviceId";

// Adjust your import paths

export function PushNotificationToggle({ cardId }) {
  const { deviceId } = useDeviceId();
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastToggleRef = useRef(0);

  // Helper for base64 decoding
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const compareUint8Arrays = (a, b) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!deviceId) return;
      try {
        const response = await checkPushNotificationSubscription(
          deviceId,
          cardId
        );
        setSubscribed(response?.subscribed ?? false);
      } catch (error) {
        console.error("Error checking subscription:", error);
        setSubscribed(false);
      } finally {
        setLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [deviceId]);

  const handleToggle = async () => {
    const now = Date.now();
    // ignore if called again within 300ms
    if (now - lastToggleRef.current < 300) return;
    lastToggleRef.current = now;

    if (subscribed) {
      const response = await unsubscribeFromPushNotification(deviceId, cardId);
      if (response?.success) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          console.log("Browser push subscription removed.");
        }
        setSubscribed(false);
      }
      return;
    }

    try {
      const { publicKey } = await fetchPushNotificationPublicKey();
      const registration = await navigator.serviceWorker.register(
        "/service-worker.js"
      );

      const applicationServerKey = urlBase64ToUint8Array(publicKey);

      let subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const existingKey = subscription.options?.applicationServerKey;

        if (
          !existingKey ||
          !compareUint8Arrays(new Uint8Array(existingKey), applicationServerKey)
        ) {
          console.log(
            "Existing subscription is invalid or outdated. Unsubscribing..."
          );
          await subscription.unsubscribe();
          subscription = null;
        }
      }

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      await subscribeToPushNotification(deviceId, cardId, subscription);
      setSubscribed(true);
    } catch (error) {
      console.error("Error subscribing:", error);
    }
  };

  if (loading) return <p>Loading subscription status...</p>;

  return (
    <div
      className="interactive-element w-content h-content"
      onClick={handleToggle}
      onTouchEnd={handleToggle}
    >
      <div className="flex flex-col items-center space-y-2">
        <Switch checked={subscribed} onChange={handleToggle} />
        <span className="text-xs text-gray-700">
          {subscribed ? "Subscribed" : "Unsubscribed"}
        </span>
      </div>
    </div>
  );
}

export default PushNotificationToggle;
